import StepType from "../flow/enums/stepType.enum"
import { Flow } from "../flow/flow.component"
import Gender from "../../enums/gender.enum"
import { useState, useContext, useRef, useEffect } from "react"
import { UserContext } from "../../contexts/user.context"
import ProductTypeCode from "../../enums/productTypeCode.enum"
import { DexaIntro } from "./dexaIntro.component.js"
import { ConfirmDexa } from "./confirmDexa.component.js"
import { listProductTypes } from '../../services/productType.service'
import { MembershipPointsModal } from "../membershipPointsModal/membersipPointsModal.component";
import { completeFlow } from "../../services/flow.service.js"
import FlowType from "../../enums/flowType.enum.js"
import { useNavigate } from "react-router-dom"
import ProductHelper from "../../helpers/product.helper"
import UserHelper from "../../helpers/user.helper"
import PayTo from "../../enums/payTo.enum.js"
export const Step = {
  INTRO: 'intro',
  ACCOUNT: 'account',
  LOCATION: 'location',
  FACILITY: 'facility',
  NAME: 'name',
  GENDER: 'gender',
  PREGNANCY: 'pregnancy',
  DOB: 'dob',
  BIOMETRICS: 'biometrics',
  INSURANCE: 'insurance',
  PHONE: 'phone',
  PAY: 'pay',
  CONFIRM: 'confirm',
}

export const DexaFlow = () => {
  const navigate = useNavigate()
  const { instalabMembership, currentUser } = useContext(UserContext)
  const [flow, setFlow] = useState()
  const [skeleton, setSkeleton] = useState()
  const [productTypes, setProductTypes] = useState()
  const [openMembershipPointsModal, setOpenMembershipPointsModal] = useState()
  const [hasPass, setHasPass] = useState()
  const hasCompletedFlowRef = useRef(false)
  const [membershipType, setMembershipType] = useState()
  const [selectedMenuItem, setSelectedMenuItem] = useState(null)

  const [initialUser, setInitialUser] = useState()

  useEffect(() => {
    fetchSkeleton()
  }, [hasPass, membershipType, productTypes, initialUser, instalabMembership, flow, selectedMenuItem])

  useEffect(() => {
    fetchProductTypes()
  }, [])

  useEffect(() => {
    if (flow?.facility && flow.facility.menu) {
      const product = flow.facility.menu.find(f => f.productType === getProductType(ProductTypeCode.DEXA_BONE)?._id);
      setSelectedMenuItem(product);
    
    }
  }, [flow, productTypes]);


  const fetchProductTypes = async () => {
    const response = await listProductTypes({
      filter: {
        code: {
          $in: [
            ProductTypeCode.DEXA_BONE,
            ProductTypeCode.CONCIERGE_FEE,
          ]
        }
      }
    })
    setProductTypes(response)
  }


  const getProductType = (code) => {
    return productTypes?.find(productType => productType.code === code)
  }

  const instructions = [
    {
      sections: [{
        title: 'Schedule Your Scan',
        description: <>Our concierge team will reach out to schedule your scan at your chosen imaging center.</>
      }, 
    ]
    }, 
    {
      sections: [{
        title: 'Complete the Scan',
        description: <>Wear comfortable clothing. The scan takes about 15 minutes, and results are usually available 24-48 hours after your appointment.</>
      }]
    },
    {
      sections: [{
        title: 'Review Your Results',
        description: <>Instalab physicians will review your results and provide personalized recommendations.</>
      }]
  }]


  const fetchSkeleton = () => {
    if (hasPass === undefined || !initialUser || !productTypes) return

    const totalCost = ProductHelper.getTotalCostV2({
      productTypes: [getProductType(ProductTypeCode.DEXA_BONE), getProductType(ProductTypeCode.CONCIERGE_FEE)],
      instalabMembership,
      currentUser,
      priceOverride: selectedMenuItem?.payTo === PayTo.INSTALAB ? [{
        code: ProductTypeCode.CLEERLY,
        price: selectedMenuItem?.price
      }] : null
    })

    const hasCredit = UserHelper.hasCredit(currentUser, ProductTypeCode.CLEERLY)

    setSkeleton({
      [Step.INTRO]: {
        type: StepType.CUSTOM,
        nextStep: Step.ACCOUNT,
        content: ({ footer }) => <DexaIntro productType={getProductType(ProductTypeCode.DEXA_BONE)} footer={footer} instructions={instructions}/>
        
      },
      [Step.ACCOUNT]: {
        type: StepType.MULTIPLE_INPUT,
        nextStep: Step.LOCATION,
        buildUser: true,
        title: `First, let's create your Instalab acccount.`,
        description: <>Already have an account? <a className="secondary-link" onClick={() => navigate(`/login?redirect=/flow/${FlowType.DEXA}`)}>Log in</a>.</>, 
        fields: [{
          name: 'email',
          label: 'Email Address',
          placeholder: 'Type your email here...',
          email: true,
          required: true,
          unique: true,
        }, {
          name: 'password',
          label: 'Password',
          placeholder: 'Type your password here...',
          password: true,
          required: true,
        }],
        skipIf: () => {
          return hasPass
        }
      },

      [Step.LOCATION]: {
        type: StepType.LOCATION,
        buildUser: true,
        buildProduct:true,
        nextStep: Step.FACILITY,
        title: `What's your current address?`,
        description: `We'll search for imaging centers near this address.`,
        field: {
          name: 'location'
        },
      },
      [Step.FACILITY]: {
        type: StepType.FACILITY,
        nextStep: Step.NAME,
        buildFlow: true,
        productTypeCode: [ProductTypeCode.DEXA_BONE, ProductTypeCode.CONCIERGE_FEE],
        showPrice: true,
        title: `Select your preferred imaging center.`,
        description: `We’ll book your DEXA scan at this facility.`,
        field: {
          name: 'facility',
          required: true,
        },
      },

      [Step.NAME]: {
        type: StepType.MULTIPLE_INPUT,
        nextStep: Step.GENDER,
        buildUser: true,
        title: `What's your full name?`,
        fields: [{
          name: 'firstName',
          placeholder: 'First Name',
          required: true,
        }, {
          name: 'lastName',
          placeholder: 'Last Name',
          required: true,
        }],
        skipIf: () => {
          return initialUser?.firstName && initialUser?.lastName
        }
      },

      [Step.GENDER]: {
        type: StepType.SINGLE_SELECT,
        onNextStep: async (patient) => {
          if (patient?.gender === Gender.FEMALE) {
            return Step.PREGNANCY
          } else {
            return Step.DOB
          }
        },
        buildUser: true,
        title: `What's your biological sex?`,
        field: {
          name: 'gender',
          options: [{
            label: 'Male',
            value: Gender.MALE,
          }, {
            label: 'Female',
            value: Gender.FEMALE,
          }]
        },
        skipIf: () => {
          return initialUser?.gender
        }
      },
      [Step.PREGNANCY]: {
        type: StepType.SINGLE_SELECT,
        nextStep: Step.DOB,
        buildUser: true,
        title: `Are you currently pregnant?`,
        description: 'Please note you should not get the scan done if there is any chance you may be pregnant.',
        field: {
          name: 'isPregnant',
          options: [{
            label: 'Yes',
            value: true,
          }, {
            label: 'No',
            value: false,
          }]
        },
        skipIf: () => {
          return initialUser?.gender === Gender.MALE
        }
      },
      [Step.DOB]: {
        type: StepType.SINGLE_INPUT,
        nextStep: Step.BIOMETRICS,
        buildUser: true,
        title: `What is your date of birth?`,
        description: `Enter in the format of MM/DD/YYYY.`,
        field: {
          name: 'dob',
          placeholder: 'MM/DD/YYYY',
          date: true,
          inputMode: 'numeric',
          required: true,
        },
        skipIf: () => {
          return initialUser?.dob
        }
      },
      [Step.BIOMETRICS]: {
        type: StepType.MULTIPLE_INPUT,
        nextStep: Step.PHONE,
        buildUser: true,
        title: `What's your current height and weight?`,
        description: `This is needed to ensure the right machine is used for the scan. Please list the reading of when you were last measured.`,
        fields: [{
          name: 'height',
          label: 'Height (inches)',
          placeholder: 'Type your height here...',
        }, {
          name: 'weight',
          label: 'Weight (lbs)',
          placeholder: 'Type your weight here...',
        }],
        skipIf: () => {
          return initialUser?.height && initialUser?.weight
        }
      },

      // [Step.INSURANCE]: {
      //   type: StepType.INSURANCE_UPLOAD,
      //   onNextStep: () => { return Step.PHONE },
      //   buildUser: true,
      //   title: 'Upload your insurance card (optional)',
      //   description: 'Some health insurance plans will cover the cost of the DEXA bone density scan for someone like you. Upload a photo of your card if you\'d like us to check.',
      //   hasSkip: true,
      //   fields: [
      //     {
      //       name: 'frontInsuranceCard',
      //       fileTypeCode: FileTypeCode.INSURANCE_CARD_FRONT,
      //       upload: true,
      //       title: 'Front of your insurance card',
      //     },
      //     {
      //       name: 'backInsuranceCard',
      //       fileTypeCode: FileTypeCode.INSURANCE_CARD_BACK,
      //       upload: true,
      //       title: 'Back of your insurance card',
      //     }
      //   ],
      // },

      [Step.PHONE]: {
        type: StepType.SINGLE_INPUT,
        nextStep: Step.PAY,
        buildUser: true,
        title: `What's your phone number?`,
        description: `We'll only send you urgent notifications about your account or results here.`,
        field: {
          name: 'phone',
          phone: true,
          inputMode: 'numeric',
          placeholder: 'Type your phone number here...',
          required: true,
          unique: true,
        },
        skipIf: () => {
          return initialUser?.phone
        }
      },


      [Step.PAY]: {
        type: StepType.PAY,
        nextStep: Step.CONFIRM,
        addProduct: true,
        priceOverride: () => {
          return (selectedMenuItem?.payTo === PayTo.INSTALAB)? [{
            code: ProductTypeCode.CLEERLY,
            price: selectedMenuItem?.price
          }] : null
        },
        productType: [ProductTypeCode.DEXA_BONE, ProductTypeCode.CONCIERGE_FEE],
        title: () => {
          
          if (selectedMenuItem?.payTo === PayTo.INSTALAB) {
            return  <>Pay <span className="true-price">${totalCost}</span> for the <span className="product-name">{getProductType(ProductTypeCode.DEXA_BONE).title}</span>.</>
          }
          else {
            return  <>Pay <span className="true-price">${totalCost}</span> for the <span className="product-name">{getProductType(ProductTypeCode.CONCIERGE_FEE).title}</span> to get started.</>
          }
        },
        description: () => {
          if (selectedMenuItem?.payTo === PayTo.INSTALAB) {  
            return <>This includes everything: your physician order, appointment scheduling, the scan itself, and a personalized results report.</>
          }
          else {
            return <>This concierge fee includes your required physician order, appointment scheduling, and a personalized results report. {!hasCredit && <p><i>Note: The remaining {selectedMenuItem?.price && ` $${selectedMenuItem?.price}`} will be paid directly to {flow?.facility ? flow?.facility.name : 'the facility'} at your appointment.</i></p>} </>
          }
        },
        skipIf: () => {
          return (totalCost === 0) || hasCredit
        },
      },

      [Step.CONFIRM]: {
        type: StepType.STATIC,
        showFooter: false,
        title: `All set!`,
        description: `We'll email you within 1 business day to schedule your scan.`,
        enterStyle: { display: 'none' },
        buttonStyle: { display: 'none' },
        content: <ConfirmDexa instructions={instructions} productType={getProductType(ProductTypeCode.DEXA_BONE)}/>,
        onLoad: async () => {
          if (flow?._id && !hasCompletedFlowRef.current) {
            hasCompletedFlowRef.current = true; // Mark flow as completed
            await completeFlow(flow._id);
          }
        }
      },
    })
  }

  return <>
    <Flow 
      skeleton={skeleton} 
      flow={flow}
      setFlow={setFlow}
      initialUser={initialUser}
      setInitialUser={setInitialUser}
      setHasPass={setHasPass}
      productTypeCode={ProductTypeCode.DEXA_BONE}
    />

    <MembershipPointsModal
      open={openMembershipPointsModal}
      setOpen={setOpenMembershipPointsModal}
    />
  </>
}